import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { DraftTags } from '~src/drafts/components/DraftTags';
import { Draft, DraftTagsParentType } from '~src/drafts/types/DraftsTypes';
import { extractWorkbookDraftEditUrlInfo, isUnpublishedDraft, isValidDraftStatus } from '~src/drafts/utils/DraftUtils';
import { FavoriteToggle } from '~src/favorites/components/favoriteToggle/FavoriteToggle';
import { CardTitle } from '~src/spacesPlatform/components/card/CardTitle';
import { CardViewStyles } from '~src/spacesPlatform/components/card/CardViewStyles';
import { CardPopoverWidget, ContentCard, getContentUrlForItem } from '~src/spacesPlatform/components/card/ContentCard';
import { Thumbnail } from '~src/spacesPlatform/components/card/Thumbnail';
import { ContentTooltipProps } from '~src/spacesPlatform/components/tooltip/TooltipHelper';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';

import { WorkbookTooltip } from '../WorkbookTooltip';

export const getWorkbookDefaultImageUrl = () => imgPath('spaces_card_workbook.svg');

export class WorkbookCard extends ContentCard {

  public render() {
    const { content, allowDragAndDrop, hovered, selected, inRecycleBinSpace } = this.props;
    const isValidDraft = isValidDraftStatus(content.draftStatus);
    const isUnpublished = isUnpublishedDraft(content.draftStatus);
    /*
      Card does not navigate or show FavoriteToggle and PopOver when in Recycle bin space.
    */
    return (
      <div className={CardViewStyles.Card}>
        <Thumbnail
          navigateUrl={inRecycleBinSpace ? null : this.getNavigationUrl()}
          openLinkInNewTab={isUnpublished}
          defaultImageUrl={getWorkbookDefaultImageUrl()}
          thumbnailImageUrl={content.thumbnailUrl}
          hovered={hovered}
          selected={selected}
          decorate={true}
          allowDragAndDrop={allowDragAndDrop}
          aria-label={content.name}
          contentType={'workbook'}
        />
        <div className={CardViewStyles.ActionRow} style={this.getCardZoomStyleForActionRow()}>
          <CardTitle name={content.name} />
          {!inRecycleBinSpace && <span>
            <CardPopoverWidget
              content={content}
              onTriggerClick={this.handleShowPopover}
              popoverContent={this.renderPopoverContent()}
              title={Messages.workbookDetails()}
            />
          </span>
          }
          {!isUnpublished && !inRecycleBinSpace && this.renderFavoriteToggle()}
          {this.renderActionsMenu()}
        </div>
        {this.shouldRenderExtraInformationAtBottom() && <div className={CardViewStyles.CardDescription}>
          {isValidDraft ? this.getDescriptionWithDraft() : this.getDescription()}
        </div>}
        {isValidDraft && this.renderDraftStatus(content.draftStatus, content.contentType)}
      </div>
    );
  }

  private renderDraftStatus(draftStatus: string, contentType: ExplorableItemType) {
    return <div style={this.getCardZoomStyleForText()}>
      <DraftTags
        explorableItemType={contentType}
        draftStatus={draftStatus}
        parentType={DraftTagsParentType.card}>
      </DraftTags>
    </div>;
  }

  private renderFavoriteToggle(): JSX.Element {
    return (
      <span>
        <FavoriteToggle
          isTabbable={true}
          isFavorite={this.props.content.favorite}
          itemName={this.props.content.name}
          onAddFavorite={() => this.props.addContentFavorite({ ...this.props.content, contentType: 'workbook' })}
          onRemoveFavorite={() => this.props.removeContentFavorite({ ...this.props.content, contentType: 'workbook' })}
        />
      </span>
    );
  }

  private getNavigationUrl = (): string | undefined => {
    const editUrlInfo = extractWorkbookDraftEditUrlInfo(this.props.content);
    return editUrlInfo.isUnpublishedWorkbookDraft ? editUrlInfo.editUrl : getContentUrlForItem(this.props.content, this.props.urls);
  };

  private getDraft = (): Draft | undefined => {
    const { draftLuid } = this.props.content;
    return (draftLuid && this.props.drafts && draftLuid in this.props.drafts) ? this.props.drafts[draftLuid] : undefined;
  };

  protected getPopoverComponent(): React.ComponentType<ContentTooltipProps> {
    return WorkbookTooltip;
  }

  protected overridePopoverProps(): Partial<ContentTooltipProps> {
    return {
      draftLastUpdatedTimeStamp: this.getDraft()?.updatedAt
    };
  }
}
